import { Loading, MaterialDataTable } from '@marketing-milk/frontend'
import React, { useState } from 'react'
import { GridColumnTypesRecord, GridCellParams, GridColDef } from '@material-ui/x-grid'
import {
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@material-ui/core'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import ScheduleIcon from '@material-ui/icons/Schedule'
import EmailIcon from '@material-ui/icons/Email'
import LinkIcon from '@material-ui/icons/Link'
import DeleteIcon from '@material-ui/icons/Delete'
import { useFetchUserInvites } from '../../../../hooks'
import { userService } from 'app/service/user'
import { toast } from 'react-toastify'
import { Dictionary } from 'lodash'
import { BusinessRaw } from 'app/interface/redux/types/data/generic/businesses'
import { UserInvite } from '@marketing-milk/interfaces'
import { CustomSelect } from '../shared/CustomSelect'

type BusinessesBadgesProps = {
  businessIDs: string[]
  businessLookUp: Dictionary<BusinessRaw>
}

const BusinessesBadges: React.FC<BusinessesBadgesProps> = ({ businessIDs, businessLookUp }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const displayLength = 2
  const businesses = businessIDs.map(bid => businessLookUp[bid]?.businessName).sort()
  const sortedBusinesses = [...businesses].sort((a, b) => a.localeCompare(b))
  const visibleBusinesses = businesses.slice(0, displayLength)
  const hasMore = businesses.length > displayLength

  const filteredBusinesses = sortedBusinesses.filter(business =>
    business.toLowerCase().includes((searchTerm || '').toLowerCase())
  )

  const handleDialogOpen = () => {
    setDialogOpen(true)
    setSearchTerm('')
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSearchTerm('')
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || ''
    setSearchTerm(value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }

  if (businesses.length === 0) {
    return <Typography></Typography>
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        {visibleBusinesses.map((business, index) => (
          <Chip
            key={index}
            label={business}
            size="small"
            color="primary"
            variant="outlined"
            style={{ fontSize: '0.75rem' }}
          />
        ))}
        {hasMore && (
          <IconButton
            size="small"
            onClick={handleDialogOpen}
            style={{ padding: 4 }}
            title={`+${businesses.length - displayLength} more businesses`}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">All Businesses ({businesses.length})</Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search businesses..."
            value={searchTerm || ''}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            style={{ marginBottom: 16 }}
            variant="outlined"
            size="small"
          />
          <List style={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredBusinesses.length === 0 ? (
              <ListItem>
                <ListItemText primary="No businesses found" />
              </ListItem>
            ) : (
              filteredBusinesses.map((business, index) => (
                <ListItem key={index} dense>
                  <ListItemText primary={business} />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export enum InviteTableTypes {
  InviteName = 'InviteName',
  Email = 'Email',
  Status = 'Status',
  Roles = 'Roles',
  SignedUpEmail = 'SignedUpEmail',
  InvitedBy = 'InvitedBy',
  Actions = 'Actions',
  Businesses = 'Businesses',
}

export const inviteTableColumns: Array<GridColDef> = [
  {
    field: 'name',
    type: InviteTableTypes.InviteName,
    headerName: 'Name',
  },
  {
    field: 'email',
    type: InviteTableTypes.Email,
    headerName: 'Delivered to',
  },
  {
    field: 'acceptedInvite',
    type: InviteTableTypes.Status,
    headerName: 'Status',
  },
  {
    field: 'roleIDs',
    type: InviteTableTypes.Roles,
    headerName: 'Roles',
  },
  {
    field: 'invitedByUser',
    type: InviteTableTypes.InvitedBy,
    headerName: 'Invited By',
  },
  {
    field: 'businessIDs',
    type: InviteTableTypes.Businesses,
    headerName: 'Invited to Businesses',
  },
  {
    field: 'signedUpEmail',
    type: InviteTableTypes.SignedUpEmail,
    headerName: 'Registered Email',
  },
  {
    field: '',
    type: InviteTableTypes.Actions,
    headerName: 'Actions',
  },
]

const inviteColTypes = (
  refetchInvites: () => void,
  businessLookUp: Dictionary<BusinessRaw>
): GridColumnTypesRecord => {
  const handleResendInvite = async (email: string) => {
    try {
      const response = await userService.resendInvite(email)
      toast.success(response.data?.message)
      refetchInvites()
    } catch (error) {
      console.error('Error resending invite:', error)
      toast.error('Failed to resend invite')
    }
  }

  const handleDeleteInvite = async (email: string) => {
    try {
      await userService.deleteInvite(email)
      toast.success(`Invite cancelled for ${email}`)
      refetchInvites()
    } catch (error) {
      console.error('Error canceling invite:', error)
      toast.error('Failed to cancel invite')
    }
  }

  const handleCopySignupLink = async (email: string) => {
    try {
      const response = await userService.getSignupLink(email)
      await navigator.clipboard.writeText(response.signupUrl)
      toast.success('Signup link copied to clipboard!')
    } catch (error) {
      console.error('Error copying signup link:', error)
      toast.error('Failed to copy signup link')
    }
  }
  return {
    [InviteTableTypes.InviteName]: {
      type: 'string',
      headerName: 'Name',
      disableColumnMenu: true,
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const name1 = `${cellParams1.row.firstName} ${cellParams1.row.lastName}`
        const name2 = `${cellParams2.row.firstName} ${cellParams2.row.lastName}`
        return name1.localeCompare(name2)
      },
      flex: 2,
      renderCell: ({ row }: GridCellParams) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography style={{ fontWeight: 500 }}>
              {`${row.firstName} ${row.lastName}`}
            </Typography>
          </div>
        )
      },
    },
    [InviteTableTypes.Email]: {
      type: 'string',
      headerName: 'Delivered to',
      disableColumnMenu: true,
      flex: 2,
      renderCell: ({ row }: GridCellParams) => {
        return <Typography>{row.email}</Typography>
      },
    },
    [InviteTableTypes.Status]: {
      type: 'string',
      headerName: 'Status',
      disableColumnMenu: true,
      flex: 2,
      renderCell: ({ row }: GridCellParams) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.acceptedInvite ? <VerifiedUserIcon color="primary" /> : <ScheduleIcon />}
            <Typography style={{ marginLeft: 8 }}>
              {row.acceptedInvite ? 'Accepted' : 'Pending'}
            </Typography>
          </div>
        )
      },
    },
    [InviteTableTypes.Roles]: {
      type: 'string',
      headerName: 'Roles',
      disableColumnMenu: true,
      flex: 2,
      sortable: false,
      renderCell: ({ row }: GridCellParams) => {
        return <Typography>{row.roles?.length ? `${row.roles.join(', ')}` : 'No roles'}</Typography>
      },
    },
    [InviteTableTypes.InvitedBy]: {
      type: 'string',
      headerName: 'Invited By',
      disableColumnMenu: true,
      flex: 2,
      renderCell: ({ row }: GridCellParams) => {
        return <Typography>{row.invitedByUser}</Typography>
      },
    },
    [InviteTableTypes.Businesses]: {
      type: 'string',
      headerName: 'Invited to Businesses',
      disableColumnMenu: true,
      flex: 3,
      renderCell: ({ row }: GridCellParams) => {
        return <BusinessesBadges businessIDs={row.businessIDs} businessLookUp={businessLookUp} />
      },
    },
    [InviteTableTypes.SignedUpEmail]: {
      type: 'string',
      headerName: 'Registered Email',
      disableColumnMenu: true,
      flex: 2,
      renderCell: ({ row }: GridCellParams) => {
        return <Typography>{row.signedUpEmail}</Typography>
      },
    },
    [InviteTableTypes.Actions]: {
      type: 'button',
      headerName: 'Actions',
      disableColumnMenu: true,
      flex: 3,
      resizable: true,
      renderCell: ({ row }: GridCellParams) => {
        return !row.acceptedInvite ? (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <Tooltip
              title={<span style={{ fontSize: '12px' }}>Resend welcome email</span>}
              arrow
              placement="top"
            >
              <Button
                size="small"
                color="primary"
                startIcon={<EmailIcon />}
                onClick={() => handleResendInvite(row.email)}
              ></Button>
            </Tooltip>
            <Tooltip
              title={<span style={{ fontSize: '12px' }}>Copy signup link</span>}
              arrow
              placement="top"
            >
              <Button
                size="small"
                color="secondary"
                startIcon={<LinkIcon />}
                onClick={() => handleCopySignupLink(row.email)}
              ></Button>
            </Tooltip>
            <Tooltip
              title={<span style={{ fontSize: '12px' }}>Cancel Invite</span>}
              arrow
              placement="top"
            >
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteInvite(row.email)}
              ></Button>
            </Tooltip>
          </div>
        ) : (
          <></>
        )
      },
    },
  }
}

export type InvitesTableProps = {
  invites: UserInvite[] | undefined
  inviteHook: ReturnType<typeof useFetchUserInvites>
  businessLookUp: Dictionary<BusinessRaw>
}

export function InvitesTable({
  invites: data,
  inviteHook: { count, pageSize, refetch, isLoading, setPaginationParams, invitedByUsers },
  businessLookUp,
}: InvitesTableProps) {
  if (isLoading) return <Loading />

  return (
    <div>
      <div className="col-6 p-1">
        <CustomSelect
          options={invitedByUsers.map(user => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
          }))}
          clearSelection={true}
          onChange={value => {
            setPaginationParams(prevParams => ({
              ...prevParams,
              filterProp: value?.length ? 'invitedByUserId' : undefined,
              filterValues: value?.map(v => v.value).toString() ?? undefined,
            }))
          }}
          placeHolderText="Filter Invited By"
          fieldName="invitedBy"
          isMulti={true}
        />
      </div>
      <MaterialDataTable
        height={'calc(100vh - 15rem)'}
        sortingOrder={['asc', 'desc']}
        rows={data ? data : []}
        rowHeight={45}
        headerHeight={50}
        density={'comfortable'}
        columnBuffer={7}
        columns={inviteTableColumns}
        columnTypes={inviteColTypes(refetch, businessLookUp)}
        pagination
        rowCount={count}
        paginationMode={'server'}
        onPageChange={change => {
          setPaginationParams(prevParams => ({ ...prevParams, page: change.page + 1 }))
        }}
        filterMode={'server'}
        pageSize={pageSize}
        scrollEndThreshold={10}
        isCellEditable={undefined}
        getRowId={row => row.email}
      />
    </div>
  )
}
